/**
 * Compliance utilities for managing disclaimer acknowledgment and session storage
 */

// Session storage key for disclaimer acknowledgment
const DISCLAIMER_ACKNOWLEDGED_KEY = 'vclaw_disclaimer_acknowledged';

/**
 * Check if user has acknowledged the disclaimer in current session
 */
export const hasAcknowledgedDisclaimer = (): boolean => {
  try {
    return sessionStorage.getItem(DISCLAIMER_ACKNOWLEDGED_KEY) === 'true';
  } catch (error) {
    // Handle cases where sessionStorage is not available (e.g., private browsing)
    console.warn('Session storage not available:', error);
    return false;
  }
};

/**
 * Mark disclaimer as acknowledged in session storage
 */
export const acknowledgeDisclaimer = (): void => {
  try {
    sessionStorage.setItem(DISCLAIMER_ACKNOWLEDGED_KEY, 'true');
  } catch (error) {
    console.warn('Could not save disclaimer acknowledgment:', error);
  }
};

/**
 * Clear disclaimer acknowledgment (useful for testing)
 */
export const clearDisclaimerAcknowledgment = (): void => {
  try {
    sessionStorage.removeItem(DISCLAIMER_ACKNOWLEDGED_KEY);
  } catch (error) {
    console.warn('Could not clear disclaimer acknowledgment:', error);
  }
};

/**
 * Custom hook for managing disclaimer modal state
 */
import { useState, useEffect } from 'react';

export const useDisclaimerModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if disclaimer has been acknowledged on component mount
    const acknowledged = hasAcknowledgedDisclaimer();
    setIsModalOpen(!acknowledged);
    setIsLoading(false);
  }, []);

  const handleAcceptDisclaimer = () => {
    acknowledgeDisclaimer();
    setIsModalOpen(false);
  };

  return {
    isModalOpen,
    isLoading,
    handleAcceptDisclaimer
  };
};